import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { SEOPages } from '../components/SEO';
import { 
  Home,
  TrendingUp, 
  AlertCircle, 
  CheckCircle, 
  ArrowRight,
  Calculator,
  DollarSign,
  Award,
  Clock,
  Shield
} from 'lucide-react';
import LeadMagnetBox from '../components/LeadMagnetBox';
import MiniSimulateurHypotheque from '../components/MiniSimulateurHypotheque';

const GuideHypothequeNeuchatel = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F12] via-[#0C1D3D] to-[#0B0F12]">
      <SEO {...SEOPages.guideHypotheque} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-red-500/10" />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-orange-400 via-red-400 to-pink-400 bg-clip-text text-transparent leading-tight">
              Guide Complet Hypothèque Suisse
            </h1>
            <p className="text-xl text-gray-300 mb-4">
              Taux actuels, fonds propres, optimisation fiscale et tout ce qu'il faut savoir
            </p>
            <p className="text-sm text-gray-400">
              Par ATOM Group SA • Mis à jour le {new Date().toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' })} • Lecture : 15 minutes
            </p>
          </div>

          {/* Lead Magnet Top */}
          <LeadMagnetBox position="top" />
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Applicabilité nationale */}
          <div className="p-5 bg-orange-500/10 border-l-4 border-orange-500 rounded-r-lg mb-8">
            <p className="text-gray-200 text-sm">
              <strong>🇨🇭 Guide applicable dans toute la Suisse</strong> • Les règles hypothécaires sont fédérales (FINMA) : fonds propres 20%, charges ≤33%, amortissement. La fiscalité varie selon les cantons, mais les principes s'appliquent partout en Suisse romande (Vaud, Genève, Neuchâtel, Fribourg, Valais). <strong>Mis à jour : {new Date().toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' })}</strong>
            </p>
          </div>
          
          {/* Introduction */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Qu'est-ce qu'une hypothèque ?</h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              L'hypothèque est un prêt immobilier garanti par le bien que vous achetez. En Suisse romande, 
              l'hypothèque est l'outil principal pour financer l'achat d'une résidence principale ou secondaire.
            </p>
            <p className="text-gray-300 mb-4 leading-relaxed">
              Contrairement à d'autres pays, le système hypothécaire suisse est unique : amortissement partiel, fonds propres minimaux de 20%, 
              et possibilité d'utiliser votre 3ème pilier pour les fonds propres.
            </p>
            
            {/* ATOM Independence */}
            <div className="mt-6 p-4 bg-orange-500/10 border-l-4 border-orange-500 rounded-r-lg">
              <p className="text-gray-200 text-sm italic">
                <strong>ATOM Group est un cabinet 100% indépendant,</strong> non lié à une banque ou assurance. 
                Nos recommandations sont objectives et basées uniquement sur votre intérêt.
              </p>
            </div>
          </div>

          {/* Lead Magnet après intro */}

          {/* Types d'hypothèques */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Home className="w-8 h-8 text-orange-400" />
              <h2 className="text-3xl font-bold text-white">Types d'hypothèques disponibles</h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full min-w-[650px]">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left py-3 px-2 md:py-4 md:px-4 text-gray-300 font-semibold text-sm md:text-base">Type</th>
                    <th className="text-left py-3 px-2 md:py-4 md:px-4 text-gray-300 font-semibold text-sm md:text-base">Durée</th>
                    <th className="text-left py-3 px-2 md:py-4 md:px-4 text-gray-300 font-semibold text-sm md:text-base">Taux 2025</th>
                    <th className="text-left py-3 px-2 md:py-4 md:px-4 text-gray-300 font-semibold text-sm md:text-base">Idéal pour</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300 text-sm md:text-base">
                  <tr className="border-b border-white/10">
                    <td className="py-3 px-2 md:py-4 md:px-4 font-medium">Taux fixe</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">2-15 ans</td>
                    <td className="py-3 px-2 md:py-4 md:px-4 text-orange-400 font-bold">1.8% - 2.5%</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">Budget stable</td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <td className="py-3 px-2 md:py-4 md:px-4 font-medium">Taux variable</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">Flexible</td>
                    <td className="py-3 px-2 md:py-4 md:px-4 text-orange-400 font-bold">1.5% - 2.0%</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">Optimisation</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-2 md:py-4 md:px-4 font-medium">Hypothèque mixte</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">Combiné</td>
                    <td className="py-3 px-2 md:py-4 md:px-4 text-orange-400 font-bold">Variable</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">Équilibre</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 p-4 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
              <p className="text-gray-200 text-sm">
                <strong>Important :</strong> Les taux évoluent quotidiennement. Ces taux sont indicatifs pour janvier 2025.
              </p>
            </div>

            <p className="text-xs text-gray-400 mt-4 text-center italic">
              Sources : Banques suisses • Comparis • Moneyland • ATOM Group SA
            </p>
          </div>

          {/* CTA après types */}
          {/* Fonds propres */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <DollarSign className="w-8 h-8 text-green-400" />
              <h2 className="text-3xl font-bold text-white">Fonds propres : minimum 20%</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              En Suisse, vous devez apporter au moins 20% du prix du bien en fonds propres. 
              Cette règle s'applique strictement pour les résidences principales dans toute la Suisse.
            </p>

            <h3 className="text-xl font-bold text-white mb-4">Composition des fonds propres</h3>

            <div className="space-y-4">
              <div className="p-6 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">✅ Minimum 10% en fonds "durs"</h4>
                <ul className="text-gray-300 text-sm space-y-1 ml-4">
                  <li>• Épargne bancaire</li>
                  <li>• Placements (actions, obligations)</li>
                  <li>• Don ou héritage</li>
                  <li>• Vente d'un bien</li>
                </ul>
              </div>

              <div className="p-6 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">✅ Maximum 10% en fonds de prévoyance</h4>
                <ul className="text-gray-300 text-sm space-y-1 ml-4">
                  <li>• Retrait anticipé 3ème pilier (3a)</li>
                  <li>• Retrait anticipé 2ème pilier (LPP)</li>
                  <li>• Nantissement LPP (mise en gage sans retrait)</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
              <p className="text-gray-200 text-sm">
                <strong>⚠️ Règle importante :</strong> En cas de rachat LPP effectué dans les 3 dernières années, 
                un retrait de 2ème pilier pour l'achat immobilier n'est pas autorisé (règle fiscale fédérale).
              </p>
            </div>

            <div className="mt-4 p-4 bg-orange-500/10 border-l-4 border-orange-500 rounded-r-lg">
              <p className="text-gray-200 text-sm italic">
                <strong>ATOM Group est un cabinet 100% indépendant.</strong> Nos recommandations sont objectives 
                et basées uniquement sur votre intérêt.
              </p>
            </div>
          </div>

          {/* Capacité financière : Règle des 33% */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Calculator className="w-8 h-8 text-blue-400" />
              <h2 className="text-3xl font-bold text-white">Capacité financière : Règle des 33%</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              Au-delà des fonds propres, les banques vérifient que vos <strong>charges hypothécaires totales ne dépassent pas 33% de votre revenu brut annuel</strong>. C'est une règle cruciale appliquée par toutes les banques suisses pour s'assurer de votre capacité de remboursement.
            </p>

            <h3 className="text-xl font-bold text-white mb-4">Calcul des charges hypothécaires</h3>

            <div className="p-6 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg mb-6">
              <p className="text-gray-200 text-sm mb-3">
                <strong>Les charges prises en compte incluent :</strong>
              </p>
              <ul className="text-gray-300 text-sm space-y-2 ml-4">
                <li>• <strong>Intérêts hypothécaires</strong> (calculés à un taux théorique de ~5%, même si le taux actuel est plus bas)</li>
                <li>• <strong>Amortissement</strong> (remboursement du 2ème rang sur 15 ans)</li>
                <li>• <strong>Charges d'entretien</strong> (estimées à 1% de la valeur du bien par an)</li>
              </ul>
            </div>

            <h3 className="text-xl font-bold text-white mb-4">Exemple concret</h3>

            <div className="p-6 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl border border-blue-500/30">
              <p className="text-white mb-4"><strong>Situation :</strong> Achat d'un bien à CHF 750'000</p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-gray-300 text-sm mb-1">Prix du bien :</p>
                  <p className="text-white font-semibold">CHF 750'000</p>
                </div>
                <div>
                  <p className="text-gray-300 text-sm mb-1">Fonds propres (20%) :</p>
                  <p className="text-white font-semibold">CHF 150'000</p>
                </div>
                <div>
                  <p className="text-gray-300 text-sm mb-1">Hypothèque :</p>
                  <p className="text-white font-semibold">CHF 600'000</p>
                </div>
              </div>

              <p className="text-gray-300 text-sm mb-3 border-t border-white/20 pt-3"><strong>Calcul des charges annuelles :</strong></p>
              
              <ul className="text-gray-300 text-sm space-y-2 ml-4 mb-4">
                <li>• Intérêts (5% de CHF 600'000) : <strong className="text-white">CHF 30'000</strong></li>
                <li>• Amortissement (2ème rang ~CHF 100'000 / 15 ans) : <strong className="text-white">CHF 6'670</strong></li>
                <li>• Entretien (1% de CHF 750'000) : <strong className="text-white">CHF 7'500</strong></li>
              </ul>

              <div className="border-t border-white/20 pt-3">
                <p className="text-lg font-bold text-blue-400 mb-2">Total charges annuelles : CHF 44'170</p>
                <p className="text-green-400 font-semibold">Revenu brut minimum requis : CHF 133'850 (44'170 / 0.33)</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
              <p className="text-gray-200 text-sm">
                <strong>⚠️ Important :</strong> Les banques utilisent un taux théorique de ~5% pour le calcul, même si les taux actuels sont plus bas. Cela garantit que vous pourrez toujours rembourser même en cas de hausse des taux.
              </p>
            </div>

            <p className="text-xs text-gray-400 mt-4 text-center italic">
              Sources : FINMA • Directives bancaires suisses 2025 • Règles Bâle III
            </p>
          </div>

          {/* CTA WhatsApp */}
          <div className="glass p-6 rounded-xl border border-green-500/30 mb-8 bg-gradient-to-r from-green-500/10 to-emerald-500/10">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="text-3xl">📲</div>
                <div>
                  <p className="text-white font-semibold text-lg">Une question rapide ?</p>
                  <p className="text-gray-300 text-sm">Réponse en 5 minutes</p>
                </div>
              </div>
              <a
                href="https://wa.me/41793754293"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all transform hover:scale-105 flex items-center gap-2 whitespace-nowrap"
              >
                Écrire sur WhatsApp
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Simulateur */}
          <MiniSimulateurHypotheque />

          {/* Amortissement */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8 text-blue-400" />
              <h2 className="text-3xl font-bold text-white">Amortissement : direct ou indirect ?</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              Vous devez amortir votre hypothèque jusqu'à atteindre deux tiers (66.67%) de la valeur du bien en 15 ans maximum. Concrètement, cela signifie rembourser le <strong>2ème rang hypothécaire</strong> (la partie entre 66.67% et 80% de la valeur du bien). Deux stratégies possibles :
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="p-6 bg-blue-500/10 rounded-xl border border-blue-500/30">
                <h4 className="font-bold text-white mb-3">📉 Amortissement direct</h4>
                <p className="text-gray-300 mb-3 text-sm"><strong>Principe :</strong></p>
                <p className="text-gray-300 text-sm mb-3">
                  Vous remboursez directement le capital de l'hypothèque. La dette diminue progressivement.
                </p>
                <p className="text-gray-300 mb-2 text-sm"><strong>Avantages :</strong></p>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Simple et transparent</li>
                  <li>• Diminution de la dette</li>
                  <li>• Moins de charge d'intérêts</li>
                </ul>
              </div>

              <div className="p-6 bg-purple-500/10 rounded-xl border border-purple-500/30">
                <h4 className="font-bold text-white mb-3">📈 Amortissement indirect (via 3a)</h4>
                <p className="text-gray-300 mb-3 text-sm"><strong>Principe :</strong></p>
                <p className="text-gray-300 text-sm mb-3">
                  Vous versez dans un 3ème pilier au lieu de rembourser directement. À l'échéance, le 3a rembourse l'hypothèque.
                </p>
                <p className="text-gray-300 mb-2 text-sm"><strong>Avantages :</strong></p>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Déduction fiscale annuelle</li>
                  <li>• Rendement du 3ème pilier</li>
                  <li>• Flexibilité patrimoniale</li>
                </ul>
              </div>
            </div>

            <div className="p-6 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg">
              <p className="text-gray-200 text-sm">
                <strong>Notre recommandation ATOM :</strong> L'amortissement indirect via 3a est souvent plus avantageux fiscalement, 
                surtout pour les revenus moyens à élevés en Suisse romande.
              </p>
            </div>
          </div>

          {/* CTA Simulation */}
          <div className="glass p-8 rounded-2xl border border-purple-500/30 mb-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10">
            <div className="text-center">
              <div className="inline-block bg-gradient-to-br from-purple-500 to-pink-600 p-4 rounded-xl mb-4">
                <Calculator className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                💡 Vous souhaitez connaître votre capacité d'emprunt exacte ?
              </h3>
              <p className="text-gray-300 mb-6">
                Obtenez une simulation complète en 45 secondes
              </p>
              <a
                href="#simulateur"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#simulateur')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-700 transition-all transform hover:scale-105 text-lg"
              >
                Lancer la simulation ATOM (gratuit – 45 secondes)
                <ArrowRight className="w-6 h-6" />
              </a>
            </div>
          </div>

          {/* Cas pratiques */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-8 h-8 text-purple-400" />
              <h2 className="text-3xl font-bold text-white">Cas pratiques</h2>
            </div>

            <div className="space-y-6">
              <div className="p-6 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30">
                <h4 className="text-xl font-bold text-white mb-3">🏠 Cas 1 : Julie & Marc, premier achat</h4>
                <p className="text-gray-300 mb-2"><strong>Situation :</strong> Couple, revenus combinés CHF 150'000, CHF 180'000 d'épargne</p>
                <p className="text-gray-300 mb-4"><strong>Objectif :</strong> Acheter un appartement de CHF 750'000</p>
                <p className="text-green-400 font-semibold mb-2">✅ Solution ATOM :</p>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                  <li>Fonds propres : CHF 150'000 (20%)</li>
                  <li>Dont CHF 100'000 épargne + CHF 50'000 retrait 3a (2 × CHF 25'000)</li>
                  <li>Hypothèque : CHF 600'000 (80%)</li>
                  <li>Taux fixe 5 ans : 2.2%</li>
                  <li>Charge mensuelle : ~CHF 2'500 (faisable avec leur revenu)</li>
                </ul>
              </div>

              <div className="p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30">
                <h4 className="text-xl font-bold text-white mb-3">🏡 Cas 2 : Thomas, cadre 45 ans</h4>
                <p className="text-gray-300 mb-2"><strong>Situation :</strong> Revenu CHF 180'000, CHF 300'000 d'épargne + LPP</p>
                <p className="text-gray-300 mb-4"><strong>Objectif :</strong> Acheter une villa de CHF 1'200'000</p>
                <p className="text-green-400 font-semibold mb-2">✅ Solution ATOM :</p>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                  <li>Fonds propres : CHF 240'000 (20%)</li>
                  <li>Dont CHF 120'000 épargne + CHF 70'000 retrait 3a + CHF 50'000 retrait LPP</li>
                  <li>Hypothèque : CHF 960'000 (80%)</li>
                  <li>Stratégie mixte : 50% fixe 10 ans + 50% SARON</li>
                  <li>Amortissement indirect via 3a pour optimisation fiscale</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Lead Magnet Middle */}

          {/* Optimisation fiscale */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-8 h-8 text-yellow-400" />
              <h2 className="text-3xl font-bold text-white">Optimisation fiscale hypothécaire</h2>
            </div>

            <h3 className="text-xl font-bold text-white mb-4">Déductions fiscales possibles</h3>

            <div className="space-y-4">
              <div className="p-6 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">✅ Intérêts hypothécaires</h4>
                <p className="text-gray-300 text-sm">
                  100% déductibles du revenu imposable. Pour une hypothèque de CHF 600'000 à 2%, 
                  cela représente CHF 12'000 de déduction annuelle.
                </p>
              </div>

              <div className="p-6 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">✅ Frais d'entretien</h4>
                <p className="text-gray-300 text-sm">
                  Déductibles au réel ou forfait (environ 10% de la valeur locative selon canton). 
                  Privilégiez le forfait en début de propriété, puis le réel après gros travaux.
                </p>
              </div>

              <div className="p-6 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">✅ Amortissement indirect via 3a</h4>
                <p className="text-gray-300 text-sm">
                  Double avantage : déduction des intérêts hypothécaires + déduction du versement 3a (max CHF 7'258/an). 
                  Économie fiscale totale importante.
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-orange-500/10 border-l-4 border-orange-500 rounded-r-lg">
              <p className="text-gray-200 text-sm italic">
                <strong>ATOM Group est un cabinet 100% indépendant.</strong> Nos recommandations sont objectives 
                et basées uniquement sur votre intérêt.
              </p>
            </div>
          </div>

          {/* Erreurs à éviter */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <h2 className="text-3xl font-bold text-white">5 Erreurs fréquentes à éviter</h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  title: "Sous-estimer les charges",
                  desc: "N'oubliez pas : intérêts + amortissement + entretien + charges. Utilisez le taux calculatoire de 5% pour évaluer votre capacité réelle."
                },
                {
                  title: "Négliger l'amortissement indirect",
                  desc: "L'amortissement via 3a offre une déduction fiscale annuelle (CHF 7'258 max). Sur 15 ans, cela représente des dizaines de milliers d'économies."
                },
                {
                  title: "Retirer tout son 3ème pilier",
                  desc: "Gardez une réserve de prévoyance. Utilisez maximum 10% des fonds propres en prévoyance pour rester équilibré."
                },
                {
                  title: "Choisir uniquement le taux le plus bas",
                  desc: "Le taux variable peut augmenter rapidement. Privilégiez la sécurité avec une partie en taux fixe, surtout si votre budget est serré."
                },
                {
                  title: "Oublier les frais annexes",
                  desc: "Notaire, registre foncier, taxes de mutation : comptez 3-5% du prix d'achat en frais. Prévoyez-les dans votre budget total."
                }
              ].map((error, index) => (
                <div key={index} className="p-6 bg-red-500/10 border-l-4 border-red-500 rounded-r-lg">
                  <h4 className="font-bold text-white mb-2">❌ {error.title}</h4>
                  <p className="text-gray-300 text-sm">{error.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* WhatsApp CTA */}

          {/* FAQ */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Questions fréquentes</h2>
            
            <div className="space-y-4">
              {[
                {
                  q: "Quel est mon pouvoir d'achat immobilier ?",
                  a: "Règle simple : votre capacité d'emprunt est d'environ 5× votre revenu annuel brut. Avec CHF 100'000 de revenu, vous pouvez viser un bien de ~CHF 625'000 (avec 20% de fonds propres)."
                },
                {
                  q: "Puis-je utiliser mon 2ème pilier pour les fonds propres ?",
                  a: "Oui, mais maximum 10% des fonds propres (donc 2% du prix du bien). Vous pouvez retirer ou nantir votre LPP. Attention à la règle des 3 ans si vous avez fait des rachats LPP récemment."
                },
                {
                  q: "Taux fixe ou variable : que choisir en 2025 ?",
                  a: "En 2025, les taux fixes restent attractifs (1.8-2.5%). Si vous privilégiez la sécurité et un budget stable, optez pour le fixe. Le variable convient si vous acceptez le risque de hausse."
                },
                {
                  q: "Comment fonctionne l'amortissement indirect ?",
                  a: "Au lieu de rembourser l'hypothèque, vous versez dans un 3ème pilier (max CHF 7'258/an). À l'échéance (retraite), le capital du 3a rembourse l'hypothèque. Avantage : déduction fiscale annuelle du versement 3a."
                },
                {
                  q: "Quels sont les frais d'achat en Suisse ?",
                  a: "Comptez 3-5% du prix : notaire (~1%), registre foncier (~0.3%), droits de mutation (~1-2.5% selon commune). Pour CHF 750'000, prévoyez CHF 22'500 - 37'500 de frais."
                }
              ].map((faq, index) => (
                <div key={index} className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-orange-500/30 transition-all">
                  <h4 className="font-bold text-white mb-2 flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    {faq.q}
                  </h4>
                  <p className="text-gray-300 text-sm ml-7">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Lead Magnet Bottom */}

          {/* Conclusion */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Conclusion</h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              L'hypothèque est un engagement sur le long terme qui nécessite une planification rigoureuse. 
              En Suisse romande, les conditions sont favorables pour les acheteurs bien préparés : taux compétitifs, 
              possibilité d'utiliser le 3ème pilier, et optimisations fiscales intéressantes.
            </p>
            <p className="text-gray-300 mb-6 leading-relaxed">
              <strong className="text-white">Ne vous lancez pas seul :</strong> une mauvaise stratégie hypothécaire 
              peut vous coûter des dizaines de milliers de francs sur la durée. ATOM Group vous accompagne pour 
              optimiser chaque aspect de votre financement immobilier.
            </p>

            <div className="p-6 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-xl border border-orange-500/30">
              <p className="text-white font-semibold mb-4">
                🎯 ATOM Group vous accompagne pour :
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  Calculer votre capacité d'emprunt réelle
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  Optimiser vos fonds propres (3a, LPP, épargne)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  Comparer les offres hypothécaires et négocier les taux
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  Définir la meilleure stratégie d'amortissement
                </li>
              </ul>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-orange-600 hover:to-red-700 transition-all transform hover:scale-105 text-lg"
              >
                Réserver mon analyse gratuite maintenant
                <ArrowRight className="w-6 h-6" />
              </Link>
              <p className="text-gray-400 text-sm mt-3">
                🕐 Rendez-vous de 30 minutes • 100% gratuit • Sans engagement
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Sticky Banner */}

      {/* Exit Intent Popup */}
    </div>
  );
};

export default GuideHypothequeNeuchatel;
